import { useCallingContext } from "@/hooks/dialer/use-dialer-context";

import { CalendlyEventI } from "@/lib/types/dialer/calendly-event";
import { glencocoClientAPI } from "@/api/glencoco";

import { handleErrorMessage } from "@/helpers/error";

import { swallow } from "@/api/legacy-swallow";

/**
 * This hook is used to book a meeting by call id and only when call is available
 * @returns {Promise<boolean>}
 */
export const useBookMeetingByCallId = () => {
  const { call, campaign } = useCallingContext();

  /**
   * This function is used to book a meeting by call id and only when call is available
   * @param {CalendlyEventI} calendlyEvent - The calendly event
   * @param {string} notes - The notes for the meeting
   * @returns {Promise<string|undefined>}
   */
  const bookMeetingByCallId = async (
    calendlyEvent: CalendlyEventI
  ): Promise<string | undefined> => {
    const API = glencocoClientAPI();

    const response = await swallow(
      API.bookMeeting(
        campaign?.id as string,
        call?.customParameters.get("call_id") as string,
        calendlyEvent.id
      )
    );

    if (response.status !== 200) {
      handleErrorMessage(
        (response as any)?.response?.data?.error_code as number,
        "Failed to book meeting. Please try again."
      );

      return;
    }

    return response.data?.event?.id;
  };

  const updateBookedMeetingByCallId = async (
    bookedMeetingId: string,
    notes: string
  ) => {
    const API = glencocoClientAPI();

    const response = await swallow(
      API.updateBookedMeeting(
        campaign?.id as string,
        call?.customParameters.get("call_id") as string,
        bookedMeetingId,
        {
          notes: notes || "",
        }
      )
    );

    if (response.status !== 200) {
      handleErrorMessage(
        (response as any)?.response?.data?.error_code as number,
        "Failed to update meeting. Please try again."
      );
      return;
    }

    return true;
  };

  return { bookMeetingByCallId, updateBookedMeetingByCallId };
};
